import { useState, useMemo } from 'react';
import { RewardChoice } from '@/components/RewardChoice';
import { StakingManager } from '@/components/StakingManager';
import { WithdrawalManager } from '@/components/WithdrawalManager';
import { IneligibleStatus } from '@/components/IneligibleStatus';
import { useTacSdk } from '@/hooks/useTacSdk';
import { apiService } from '@/services/apiService';
import { TacService } from '@/services/tacService';
import type { RewardMode } from '@/types';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { ENVS } from '@/constants/config';
import { flipAddressType } from '@/utils/ton';
import useSWR from 'swr';
import { Header } from '@/components/Header';
import primaryBg from '@/assets/primary-bg.png';
import { Badge, Popover, toast } from '@ethsign/ui';
import { CoinsStacked03, InfoCircle } from '@ethsign/icons';
import { formatAmount, formatTokenAmount, isAfterByUnix } from '@/utils/common';
import { Loading } from '@/components/Loading';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ProjectEndPanel } from '@/components/AirdropPanel';

export function Home() {
  const [isProcessing, setIsProcessing] = useState(false);

  const { tacSdk, createSender } = useTacSdk();
  const [tonConnectUI] = useTonConnectUI();

  const [searchParams] = useSearchParams();
  const searchAddr = searchParams.get('address');

  const address = useMemo(() => {
    if (searchAddr) {
      return searchAddr;
    }
    return tonConnectUI.account?.address ? flipAddressType(tonConnectUI.account?.address) : '';
  }, [searchAddr, tonConnectUI.account?.address]);

  const tacService = useMemo(() => {
    if (!tacSdk) return undefined;
    return new TacService({
      tacSdk,
      contractAddress: ENVS.TAC_VESTING_CONTRACT_ADDRESS,
      chain: ENVS.TAC_CHAIN,
      distributionAddress: ENVS.TAC_DISTRIBUTION_CONTRACT_ADDRESS
    });
  }, [tacSdk]);

  const { data: eligibility } = useSWR(address ? ['eligibility', address] : null, () =>
    apiService.checkEligibility(address)
  );

  console.log(address, eligibility, 'eligibility');

  const {
    data: userData,
    mutate: mutateUserData,
    isLoading: isChecking
  } = useSWR(tacService && address ? ['userInfo', address] : null, async () => {
    if (!tacService) return null;
    const hasChosen = await tacService.hasUserMadeChoice(address);

    if (hasChosen) {
      const choiceMode = await tacService.getUserChoiceMode(address);
      const userInfo = await tacService.getUserInfo(address);

      return {
        mode: choiceMode,
        userInfo,
        hasChosen
      };
    } else {
      return {
        hasChosen
      };
    }
  });

  // Fetch latest amounts from contract using SWR
  const {
    data: contractAmounts,
    mutate: refreshAmounts,
    isLoading: isRefreshingAmounts
  } = useSWR(tacService && address ? ['contractAmounts', address] : null, async () => {
    if (!tacService) return null;

    const [available, unlocked] = await Promise.all([
      tacService.getAvailable(address),
      tacService.getUnlocked(address)
    ]);
    return { available, unlocked };
  });

  // Fetch next unlock time information
  const {
    data: unlockInfo,
    mutate: refreshUnlockInfo,
    isLoading: isRefreshingUnlockInfo
  } = useSWR(tacService && address ? ['unlockInfo', address] : null, async () => {
    if (!tacService) return null;
    const unlockData = await tacService.calculateNextUnlockTime(address);
    return unlockData;
  });

  // Refresh both amounts and unlock info
  const handleRefreshData = () => {
    refreshAmounts();
    refreshUnlockInfo();
  };

  console.log(userData, unlockInfo, contractAmounts, 'user');

  const handleModeSelect = async (mode: RewardMode) => {
    console.log(tacService, address, eligibility, mode);
    if (!tacService || !address || !eligibility || !mode) {
      toast({
        variant: 'error',
        title: 'Missing Data',
        description: 'Please ensure all required data is available before making a choice.'
      });
      return;
    }

    setIsProcessing(true);

    try {
      const sender = await createSender(tonConnectUI);

      console.log('start sending tx:', { mode, sender });

      let transactionResult;

      if (mode === 'staking') {
        transactionResult = await tacService.chooseStaking(
          {
            validatorAddress: ENVS.TAC_VALIDATOR_ADDRESS,
            userTotalRewards: eligibility.rewardAmount,
            merkleProof: eligibility.merkleProof
          },
          sender
        );
      } else {
        transactionResult = await tacService.chooseImmediateWithdraw(
          {
            userTotalRewards: eligibility.rewardAmount,
            merkleProof: eligibility.merkleProof
          },
          sender
        );
      }

      console.log('tx result:', transactionResult);

      toast({
        variant: 'loading',
        title: 'Transaction Pending',
        description: 'Transaction sent, waiting for confirmation...',
        duration: Infinity
      });

      // 等待交易确认
      const res = await tacService.waitForTransactionConfirmation(transactionResult, mode === 'staking' ? false : true);

      console.log(res, 'res');

      toast({
        variant: 'success',
        title: 'Transaction Confirmed',
        description: `${mode === 'staking' ? 'Staking' : 'Immediate withdrawal'} operation completed successfully!`
      });

      // Refresh all data after successful transaction
      mutateUserData();
      handleRefreshData();
    } catch (error) {
      console.error('交易处理失败:', error);
      toast({
        variant: 'error',
        title: 'Transaction Failed',
        description: error instanceof Error ? error.message : 'Transaction failed'
      });
      mutateUserData();
      handleRefreshData();
    } finally {
      setIsProcessing(false);
    }
  };

  const isEndClaim = isAfterByUnix(dayjs(ENVS.endTime).unix());
  if (userData && isEndClaim && !userData?.hasChosen) {
    return <ProjectEndPanel />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {isChecking && <Loading />}
        <div className="space-y-6">
          {eligibility ? (
            <div
              className={'p-6 rounded-[16px] flex flex-col items-center gap-4 relative'}
              style={{
                background: `url(${primaryBg}) lightgray 50% / cover no-repeat`
              }}
            >
              <img
                src={'/logo.jpg'}
                className={'size-[100px] rounded-full border-[2.5px] border-primary-foreground'}
                alt=""
              />
              <Badge
                className={
                  'flex items-center gap-2 bg-secondary-cardBackground text-secondary-foreground text-xs font-medium absolute top-3 left-3'
                }
                variant={'gray'}
              >
                <div className={'size-2 bg-slime-500 rounded-full'}></div>
                Live
              </Badge>
              {userData?.hasChosen ? (
                <div className={'text-primary-foreground text-xs font-semibold'}>You have already Unlocked</div>
              ) : (
                <div className={'text-xs font-semibold text-primary-foreground flex gap-2 items-center'}>
                  <span>$TAC Awaiting Claim</span>
                  <Popover
                    trigger={
                      <span>
                        <InfoCircle size={14} color={'#FFF'} />
                      </span>
                    }
                    contentProps={{
                      className: 'w-[247px] py-1 px-2',
                      side: 'bottom',
                      align: 'end'
                    }}
                  >
                    <div className={'text-xs font-medium'}>
                      The total amount of your ongoing claims and upcoming claims
                    </div>
                  </Popover>
                </div>
              )}

              <div className={'text-lg font-bold text-primary-foreground flex justify-center items-center gap-2'}>
                <CoinsStacked03 className={'size-7'} color={'#FCFCFD'} />
                <span
                  className={'font-Number text-[22px]'}
                  style={{
                    textShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
                  }}
                >
                  {userData?.hasChosen
                    ? formatTokenAmount(userData?.userInfo?.unlocked)
                    : formatAmount(eligibility?.totalRewards)}
                </span>
                <span>$TAC</span>
              </div>

              {/* {nextClaim ? <CutdownPanel data={nextClaim} token={token} refresh={refetch} /> : null} */}
            </div>
          ) : null}
          {eligibility?.eligible && !userData?.hasChosen && !isChecking && (
            <RewardChoice
              totalRewards={eligibility.totalRewards}
              onModeSelect={handleModeSelect}
              loading={isProcessing}
            />
          )}

          {/* Show staking manager for staking users */}
          {userData?.hasChosen && userData?.mode === 'staking' && userData?.userInfo && (
            <StakingManager
              userInfo={userData.userInfo}
              tacService={tacService!}
              onTransactionStart={() => setIsProcessing(true)}
              onTransactionComplete={() => {
                setIsProcessing(false);
                mutateUserData();
                handleRefreshData();
              }}
              userTonAddress={address}
              contractAmounts={contractAmounts}
              unlockInfo={unlockInfo}
              isRefreshing={isRefreshingAmounts || isRefreshingUnlockInfo}
              onRefresh={handleRefreshData}
            />
          )}

          {/* Show withdrawal manager for immediate withdrawal users */}
          {userData?.hasChosen && userData?.mode === 'immediate' && userData?.userInfo && (
            <WithdrawalManager userInfo={userData.userInfo} />
          )}

          {/* Show ineligible status for users who are not eligible */}
          {eligibility && !eligibility.eligible && !isChecking && <IneligibleStatus />}
        </div>
      </div>
    </div>
  );
}
